import { BOOLEAN_CHOICES } from 'constants/booleanChoices';

import { differenceInDays } from 'date-fns';
import { required } from 'react-admin';
import { Controller, useFormContext } from 'react-hook-form';
import { DEFAULT_DATE_MASK } from 'shared/mui/DatePicker/config/defaultValues';
import { DateRangeTimezonePicker } from 'shared/mui/DatePicker/DateRangeTimezonePicker/DateRangeTimezonePicker';
import { FilterFieldConfig } from 'shared/mui/Filter/config';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import { useFilterTimezone } from 'shared/mui/Filter/hooks/useFilterTimezone';
import { TextField } from 'shared/mui/TextField/TextField';
import {
  AutocompleteInput,
  AutocompleteMultiselectInput,
  SelectInput,
} from 'shared/react-admin/Inputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { Currency } from 'types/embedded/currency';

export const useCumulativeReportFilterFields = (): FilterFieldConfig[] => {
  const { resetField, setValue, watch, control, formState } = useFormContext();
  const { timezone, onChangeTimezone } = useFilterTimezone();
  const { setAppliedFilter, appliedFilters, onReset } = useFilterContext();
  const [company, merchant, provider] = watch(['company', 'merchant', 'provider']);

  return [
    {
      name: 'appliedDate',
      label: 'Период отчёта',
      chipType: 'dateTimeRangeTimezone',
      chipProps: {
        mask: DEFAULT_DATE_MASK,
        onDelete: onReset,
      },
      input: (
        <Controller
          control={control}
          name="appliedDate"
          render={({ field: { onChange, onBlur, value }, formState: { errors }, fieldState }) => (
            <DateRangeTimezonePicker
              error={!!errors.appliedDate}
              helperText={fieldState?.error?.message}
              label="Период отчёта от-до"
              maxDate={new Date()}
              onBlur={onBlur}
              onChange={onChange}
              onChangeTimezone={onChangeTimezone}
              required
              timezone={timezone}
              value={value}
            />
          )}
          rules={{
            validate: {
              required: (value) => (!!value[0] && !!value[1]) || 'Обязательное поле',
              periodError: (value) => {
                const [from, to] = value;
                const intervalInDays = differenceInDays(to, from);
                return intervalInDays < 31;
              },
            },
          }}
        />
      ),
    },
    {
      name: 'accountsSummary',
      label: 'Сводка по счетам',
      chipType: 'text',
      chipProps: {
        getName: (filterValue: boolean) =>
          BOOLEAN_CHOICES.find((v) => filterValue === v.id)?.name || '',
        onDelete: onReset,
      },
      input: (
        <SelectInput
          choices={BOOLEAN_CHOICES}
          defaultValue={false}
          fullWidth
          helperText={false}
          id="accountsSummary"
          label="Сводка по счетам"
          name="accountsSummary"
          source="accountsSummary"
          validate={required()}
          variant="outlined"
        />
      ),
    },
    {
      name: 'currency',
      label: 'Валюта конвертации',
      chipType: 'text',
      chipProps: {
        onDelete: onReset,
      },
      input: (
        <ReferenceInput
          filter={{ active: true }}
          name="currency"
          perPage={1000}
          reference="currencies"
          resettable
          resource="currencies"
          sort={{ field: 'id', order: 'ASC' }}
          source="currency"
        >
          <AutocompleteInput
            clearOnBlur={false}
            filterToQuery={(searchText: string) => ({ currency: searchText })}
            helperText={false}
            label="Валюта конвертации"
            name="currency"
            optionText={(record: Currency) => `${record?.alphaCode}, ${record?.name}`}
            optionValue="alphaCode"
            validate={required()}
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
    {
      name: 'company',
      label: 'Компания',
      chipType: 'autocomplete',
      chipProps: {
        disabled: true,
        resource: 'companies',
        onApply: () => {
          setValue('merchant', null);
          setValue('provider', null);
          setValue('merchantAccounts', []);

          setAppliedFilter('merchant', null);
          setAppliedFilter('provider', null);
          setAppliedFilter('merchantAccounts', []);
        },
        onDelete: onReset,
        onReset: () => {
          setValue('merchant', appliedFilters?.['merchant']);
          setValue('provider', appliedFilters?.['provider']);
          setValue('merchantAccounts', appliedFilters?.['merchantAccounts']);

          setAppliedFilter('merchant', appliedFilters?.['merchant']);
          setAppliedFilter('provider', appliedFilters?.['provider']);
          setAppliedFilter('merchantAccounts', appliedFilters?.['merchantAccounts']);
        },
      },
      input: (variant) => (
        <ReferenceInput
          filter={{ exists: { 'merchants.merchantAccounts': true }, archive: false }}
          perPage={100}
          reference="companies"
          source="company"
        >
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Компания"
            limitTags={1}
            name="company"
            {...(variant === 'field' && {
              onChange: () => {
                setValue('merchant', null);
                setValue('provider', null);
                setValue('merchantAccounts', []);
              },
            })}
            optionText="name"
            validate={required()}
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
    {
      name: 'merchant',
      label: 'Мерчант',
      chipType: 'autocomplete',
      chipProps: {
        resource: 'merchants',
        onApply: () => {
          setValue('provider', null);
          setValue('merchantAccounts', []);

          setAppliedFilter('provider', null);
          setAppliedFilter('merchantAccounts', []);
        },
        onDelete: onReset,
        onReset: () => {
          setValue('provider', appliedFilters?.['provider']);
          setValue('merchantAccounts', appliedFilters?.['merchantAccounts']);

          setAppliedFilter('provider', appliedFilters?.['provider']);
          setAppliedFilter('merchantAccounts', appliedFilters?.['merchantAccounts']);
        },
      },
      input: (variant) => (
        <ReferenceInput
          enableGetChoices={() => !!company?.length}
          filter={{ companies: company, exists: { merchantAccounts: true }, archive: false }}
          perPage={100}
          reference="merchants"
          source="merchant"
        >
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Мерчант"
            limitTags={1}
            name="merchant"
            {...(variant === 'field' && {
              onChange: () => {
                setValue('provider', null);
                setValue('merchantAccounts', []);
              },
            })}
            optionText="name"
            validate={required()}
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
    {
      name: 'provider',
      label: 'Провайдер',
      chipType: 'autocomplete',
      chipProps: {
        resource: 'providers',
        onApply: () => {
          setValue('merchantAccounts', []);
          setAppliedFilter('merchantAccounts', []);
        },
        onDelete: () => {
          resetField('merchantAccounts', { keepDirty: true });
          setAppliedFilter('merchantAccounts', []);
        },
        onReset: () => {
          setValue('merchantAccounts', appliedFilters?.['merchantAccounts']);
          setAppliedFilter('merchantAccounts', appliedFilters?.['merchantAccounts']);
        },
      },
      input: (variant) => (
        <ReferenceInput
          enableGetChoices={() => !!merchant?.length}
          filter={{ merchantAccounts: { merchants: merchant } }}
          name="provider"
          perPage={100}
          reference="providers"
          sort={{ field: 'name', order: 'ASC' }}
          source="provider"
        >
          <AutocompleteInput
            {...(variant === 'field' && {
              disabled: !merchant,
              onChange: () => {
                setValue('merchantAccounts', []);
              },
            })}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Провайдер"
            limitTags={1}
            name="provider"
            optionText="name"
            renderInput={(params) => (
              <TextField
                {...params}
                error={!!formState.errors.provider}
                label="Провайдер"
                placeholder={params.disabled ? 'Сначала выберите мерчанта' : undefined}
                size="small"
                variant="outlined"
              />
            )}
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
    {
      name: 'merchantAccounts',
      label: 'Счета мерчантов',
      chipType: 'multiselect',
      chipProps: {
        resource: 'merchant_accounts',
        limitTags: 1,
      },
      input: (variant) => (
        <ReferenceArrayInput
          enableGetChoices={() => !!provider}
          filter={{ merchants: merchant, provider }}
          perPage={100}
          reference="merchant_accounts"
          source="merchantAccounts"
        >
          <AutocompleteMultiselectInput
            {...(variant === 'field' && {
              disabled: !provider,
              disabledHint: 'Сначала выберите провайдера',
            })}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Счета мерчантов"
            name="merchantAccounts"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
  ];
};
